import { useQuery } from '@tanstack/react-query';
import { Header } from '@/components/layout/Header';
import { Hero } from '@/components/sections/Hero';
import { Footer } from '@/components/sections/Footer';
import { SEO } from '@/components/SEO';
import { SkipLink } from '@/components/SkipLink';
import { ProductCard, Product } from '@/components/store/ProductCard';
import { useCart } from '@/contexts/CartContext';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchProducts, mapWooCommerceProduct } from '@/lib/woocommerce';

const Index = () => {
  const location = useLocation();
  const { addToCart } = useCart();

  // Recupera prodotti in evidenza (featured)
  // Nota: WooCommerce non supporta direttamente orderby='featured'
  // Usiamo 'menu_order' per prodotti ordinati o 'date' per i più recenti
  const { data: productsData, isLoading } = useQuery({
    queryKey: ['woocommerce-featured-products'],
    queryFn: () => fetchProducts({
      per_page: 4,
      orderby: 'menu_order', // Usa menu_order invece di featured
      order: 'asc',
    }),
    retry: false, // Disabilita completamente i retry
    refetchOnMount: false, // Non refetch quando il componente viene montato
    refetchOnWindowFocus: false, // Non refetch quando la finestra torna in focus
    refetchOnReconnect: false, // Non refetch quando si riconnette
    staleTime: 30 * 60 * 1000, // Cache per 30 minuti (allineato con cache server)
    gcTime: 60 * 60 * 1000, // Mantieni in cache per 60 minuti
  });

  const featuredProducts: Product[] = productsData?.products
    ? productsData.products.map(mapWooCommerceProduct)
    : [];

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const storeStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Store',
    name: 'Brace e Sapori Store',
    image: 'https://store.braceesapori.it/logo.png',
    '@id': 'https://store.braceesapori.it',
    url: 'https://store.braceesapori.it',
    telephone: '+393472916493',
    email: 'braceesapori@yahoo.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Via P. B. Moscatelli, 8',
      addressLocality: 'San Giorgio Ionico',
      postalCode: '74027',
      addressRegion: 'TA',
      addressCountry: 'IT',
    },
    priceRange: '€€',
    sameAs: [
      'https://www.instagram.com/braceesapori/',
      'https://www.facebook.com/bracesaporibraceria/',
      'https://www.tiktok.com/@bracesapori',
    ],
  };

  return (
    <>
      <SkipLink />
      <main id="main-content" className="min-h-screen bg-background">
        <SEO
          title="Brace e Sapori Store - Costate e Prodotti Tipici Pugliesi Online"
          description="Acquista online costate di alta qualità e prodotti tipici pugliesi da Brace e Sapori. Spedizione in tutta Italia. Tagli pregiati di carne, salumi, formaggi e specialità della Puglia."
          keywords="costate online, prodotti tipici pugliesi, carne online, salumi pugliesi, formaggi puglia, e-commerce carne, costate italia, prodotti tipici puglia online, brace e sapori store, brisket texas style, costate puglia"
          ogImage="/logo.png"
          structuredData={storeStructuredData}
        />
        <Header />
        <Hero />
        
        {/* Sezione Prodotti in Evidenza */}
        <section id="prodotti" className="py-16 px-4 md:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">Prodotti in Evidenza</h2>
              <p className="text-muted-foreground">
                Scopri la nostra selezione di costate e prodotti tipici pugliesi
              </p>
            </div>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">Caricamento prodotti...</span>
              </div>
            ) : featuredProducts.length > 0 ? (
              <>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {featuredProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onAddToCart={addToCart}
                    />
                  ))}
                </div>
                
                <div className="mt-12 text-center">
                  <Button size="lg" asChild>
                    <a href="/catalogo">Vedi Tutti i Prodotti</a>
                  </Button>
                </div>
              </>
            ) : (
              <div className="py-16 text-center">
                <p className="text-muted-foreground mb-4">
                  Nessun prodotto disponibile al momento.
                </p>
                <Button size="lg" asChild>
                  <a href="/catalogo">Vai al Catalogo</a>
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Sezione Chi Siamo */}
        <section id="chi-siamo" className="bg-muted py-16 px-4 md:px-6">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">Chi Siamo</h2>
            <p className="mb-4 text-lg text-muted-foreground">
              Brace e Sapori è una braceria rinomata a San Giorgio Ionico, nel cuore della provincia di Taranto.
              La nostra passione per la carne di qualità e i prodotti tipici pugliesi ci ha portato a creare
              questo store online per portare i nostri sapori direttamente a casa tua.
            </p>
            <p className="text-lg text-muted-foreground">
              Selezioniamo solo i migliori tagli di carne e prodotti tipici della nostra terra,
              garantendo qualità, freschezza e autenticità in ogni ordine.
            </p>
          </div>
        </section>

        {/* Sezione Contatti */}
        <section id="contatti" className="py-16 px-4 md:px-6">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">Contatti</h2>
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <h3 className="mb-4 text-xl font-semibold">Indirizzo</h3>
                <p className="text-muted-foreground">
                  Via P. B. Moscatelli, 8<br />
                  74027 San Giorgio Ionico (TA)
                </p>
              </div>
              <div>
                <h3 className="mb-4 text-xl font-semibold">Contatti</h3>
                <p className="text-muted-foreground">
                  <a href="tel:+393472916493" className="hover:text-primary">
                    347 291 6493
                  </a>
                  <br />
                  <a href="mailto:braceesapori@yahoo.com" className="hover:text-primary">
                    braceesapori@yahoo.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default Index;
